import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Route, Switch } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch as SwitchToggle } from "@/components/ui/switch";
import { ListFilter, Search, Plus, Pencil, Trash2, ArrowLeft, Loader2 } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { useToast } from "@/hooks/use-toast";

type LocationsState = {
  searchQuery?: string;
  currentPage?: number;
  itemsPerPage?: number;
  dateRange?: { from: string; to: string };
  active?: boolean;
  status?: string;
};

const defaultLocationsState: LocationsState = {
  searchQuery: "",
  currentPage: 1,
  itemsPerPage: 10,
  status: "",
};

const fetchLocations = async () => {
  const res = await posApiFetch("/v1/pos/locations");
  if (!res.ok) throw new Error("Failed to fetch locations");
  return res.json();
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

function LocationsListPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = "Locations | SECH Admin";
  }, []);

  const [locationsState, setLocationsState] = usePagePersistentState<LocationsState>("locations", defaultLocationsState);
  const searchQuery = locationsState.searchQuery;
  const currentPage = locationsState.currentPage;
  const itemsPerPage = locationsState.itemsPerPage;
  const statusFilter = locationsState.status;
  const dateRange = locationsState.dateRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "locations_table",
    defaultColumn: "createdAt",
    defaultDirection: "desc",
  });

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: "",
    dateRange: { from: "", to: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: statusFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
    }));
  }, [statusFilter, dateRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setLocationsState((prev) => ({
      ...prev,
      status: values.status,
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
    }));
  };

  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/locations"],
    queryFn: fetchLocations,
    retry: 3,
    retryDelay: 1000,
  });

  const getFilterCount = () => {
    let count = 0;
    if (locationsState.status && locationsState.status !== "" && locationsState.status !== "all") count += 1;
    const fromDate = normalizeDate(locationsState.dateRange?.from);
    const toDate = normalizeDate(locationsState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    return count;
  };

  const locationFilters: FilterConfig[] = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Status" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
  ], []);

  const handleClear = () => {
    setFilterValues({ status: "", dateRange: { from: "", to: "" } });
    setLocationsState((prev) => ({ ...prev, status: "", dateRange: { from: "", to: "" }, currentPage: 1 }));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await posApiFetch(`/v1/pos/locations/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete location");
      toast({ title: "Location deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/locations"] });
    } catch (err: any) {
      toast({ title: "Failed to delete location", description: err?.message || "Unknown error", variant: "destructive" });
    } finally {
      setIsDeleting(false);
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const tableColumns = useMemo(() => [
    {
      label: "Name", field: "name", sortable: true,
      cellTemplate: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          <NameAvatar name={value || ""} imageUrl={row?.imageUrl} />
          <span className="text-[13px] text-[#505258]">{value}</span>
        </div>
      ),
    },
    {
      label: "Color Code", field: "colorCode",
      cellTemplate: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="h-5 w-5 rounded-[4px] inline-block" style={{ backgroundColor: value || "#ccc" }} />
          <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "-"}</span>
        </div>
      ),
    },
    {
      label: "Status", field: "active", sortable: true,
      cellTemplate: (value: boolean) => {
        const status = value ? "active" : "inactive";
        return (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColorCode(status)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(status)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </span>
        );
      },
    },
    {
      label: "Label Enabled", field: "label",
      cellTemplate: (value: string | null) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value ? "Yes" : "No"}</span>
      ),
    },
    {
      label: "Display Order", field: "displayOrder", sortable: true,
      cellTemplate: (value: number | null) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value ?? "-"}</span>
      ),
    },
    {
      label: "Date & Time", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => navigate(`/admin/locations/${r.id}/edit`) },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], []);

  const filteredLocations = useMemo(() =>
    locations.filter((loc: any) => {
      const search = debouncedSearch.toLowerCase();
      const matchesSearch = !search || loc.name?.toLowerCase().includes(search) || loc.colorCode?.toLowerCase().includes(search);
      const locStatus = loc.active ? "active" : "inactive";
      const matchesStatus = !statusFilter || statusFilter === "" || statusFilter === "all" || locStatus === statusFilter;
      const fromDate = normalizeDate(locationsState.dateRange?.from);
      const toDate = normalizeDate(locationsState.dateRange?.to);
      const createdAt = new Date(loc.createdAt).getTime();
      const matchesDateRange = fromDate && toDate ? createdAt >= fromDate.getTime() && createdAt <= toDate.getTime() : true;
      return matchesSearch && matchesStatus && matchesDateRange;
    }), [locations, debouncedSearch, statusFilter, locationsState.dateRange]
  );

  const sortedLocations = useMemo(() => {
    return [...filteredLocations].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "name": aVal = a?.name ?? ""; bVal = b?.name ?? ""; break;
        case "active": aVal = a?.active ? 1 : 0; bVal = b?.active ? 1 : 0; break;
        case "displayOrder": aVal = a?.displayOrder ?? 0; bVal = b?.displayOrder ?? 0; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        default: aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredLocations, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedLocations.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedLocations = sortedLocations.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setLocationsState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setLocationsState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedLocations.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Locations</h2>
          <p className="text-gray-600 mb-4">Failed to load locations</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Locations</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setLocationsState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={() => navigate("/admin/locations/add")} className="h-[32px] px-3 text-[13px] font-medium bg-[var(--primary-color)] text-white rounded-[4px] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Location
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-3 h-[calc(100vh-130px)]">
          {!paginatedLocations.length && !isLoading ? (
            <NoRecordFound title="Locations" description="There are no locations available." />
          ) : (
            <CommonGrid
              rowData={paginatedLocations}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>

      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={locationFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDelete} title="Delete Location" description="Are you sure you want to delete this location? This action cannot be undone." confirmText={isDeleting ? "Deleting..." : "Delete"} variant="delete" />
    </div>
  );
}

// --- Shared location editor used by both Add and Edit ---

interface LocationFormState {
  name: string;
  description: string;
  colorCode: string;
  active: boolean;
  displayOrder: string;
  label: string;
}

const EMPTY_LOCATION: LocationFormState = {
  name: "",
  description: "",
  colorCode: "#000000",
  active: true,
  displayOrder: "0",
  label: "",
};

function LocationEditor({ locationId }: { locationId?: string }) {
  const isEdit = !!locationId;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<LocationFormState>(EMPTY_LOCATION);
  const [isSaving, setIsSaving] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = isEdit ? "Edit Location | SECH Admin" : "Add Location | SECH Admin";
  }, [isEdit]);

  const { data: existingLocation, isLoading } = useQuery<any>({
    queryKey: ["/v1/pos/locations", locationId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/locations/${locationId}`);
      if (!res.ok) throw new Error("Failed to fetch location");
      return res.json();
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existingLocation) return;
    const l = existingLocation;
    setForm({
      name: l.name ?? "",
      description: l.description ?? "",
      colorCode: l.colorCode ?? "#000000",
      active: l.active ?? true,
      displayOrder: l.displayOrder != null ? String(l.displayOrder) : "0",
      label: l.label ?? "",
    });
  }, [existingLocation]);

  const set = <K extends keyof LocationFormState>(key: K, value: LocationFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: "Validation Error", description: "Name is required", variant: "destructive" });
      return;
    }
    if (!form.description.trim()) {
      toast({ title: "Validation Error", description: "Description is required", variant: "destructive" });
      return;
    }
    if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(form.colorCode)) {
      toast({ title: "Validation Error", description: "Color code must be valid hex (e.g. #FF5733)", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        colorCode: form.colorCode,
        active: form.active,
        displayOrder: parseInt(form.displayOrder) || 0,
        label: form.label.trim() || null,
      };

      const url = isEdit ? `/v1/pos/locations/${locationId}` : "/v1/pos/locations";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} location`);
      }

      toast({ title: `Location ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/locations"] });
      navigate("/admin/locations");
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Failed to save location", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={() => navigate("/admin/locations")} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Locations
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={() => navigate("/admin/locations")} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Locations
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit Location" : "Create Location"}
        </h2>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Name *</Label>
                <Input className="h-[36px] text-[13px]" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Main Bar, Pool Deck" />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Color Code *</Label>
                <div className="flex items-center gap-2">
                  <input ref={colorInputRef} type="color" value={form.colorCode} onChange={(e) => set("colorCode", e.target.value)} className="absolute w-0 h-0 overflow-hidden opacity-0" tabIndex={-1} />
                  <button type="button" onClick={() => colorInputRef.current?.click()} className="h-[36px] w-[36px] rounded border border-input cursor-pointer p-0.5 flex-shrink-0">
                    <span className="block w-full h-full rounded-sm" style={{ backgroundColor: form.colorCode }} />
                  </button>
                  <Input className="h-[36px] text-[13px] flex-1" value={form.colorCode} onChange={(e) => set("colorCode", e.target.value)} placeholder="#FF5733" />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[12px]">Description *</Label>
              <Textarea className="text-[13px] min-h-[80px]" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Location description" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Settings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Display Order</Label>
                <Input className="h-[36px] text-[13px]" type="number" min="0" value={form.displayOrder} onChange={(e) => set("displayOrder", e.target.value)} placeholder="0" />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Label</Label>
                <Input className="h-[36px] text-[13px]" value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="Optional label text" />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <SwitchToggle checked={form.active} onCheckedChange={(v) => set("active", v)} />
              <Label className="text-[12px]">Active</Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="h-[36px] text-[13px]" onClick={() => navigate("/admin/locations")} disabled={isSaving}>Cancel</Button>
          <Button type="submit" className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]" disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Location" : "Create Location"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function LocationsPage() {
  return (
    <Switch>
      <Route path="/admin/locations/add">
        <LocationEditor />
      </Route>
      <Route path="/admin/locations/:id/edit">
        {(params) => <LocationEditor locationId={params.id} />}
      </Route>
      <Route path="/admin/locations" component={LocationsListPage} />
    </Switch>
  );
}
